<template>
  <div>
    <div class="text-center my-10">
      <h1 class="page-title text-primary">Unsere Leistungen</h1>
      <p class="page-subtitle">
        Maßgefertigte Lösungen aus Holz – von der Idee bis zur Montage
      </p>
    </div>

    <!-- Quick navigation -->
    <v-card class="bg-surface pa-4 mb-8 card-clean" elevation="1">
      <div class="d-flex flex-wrap justify-center ga-2">
        <v-chip
          v-for="cat in categories"
          :key="cat.id"
          variant="outlined"
          color="accent"
          :href="'#' + cat.id"
          @click.prevent="scrollTo(cat.id)"
        >
          <v-icon start size="18">{{ cat.icon }}</v-icon>
          {{ cat.title }}
        </v-chip>
      </div>
    </v-card>

    <!-- Service Categories -->
    <section
      v-for="(cat, idx) in categories"
      :key="cat.id"
      :id="cat.id"
      class="service-section"
    >
      <v-card
        class="pa-6 pa-md-8 mb-6 card-clean"
        :class="idx % 2 === 0 ? 'bg-surface' : 'bg-primary'"
        :elevation="idx % 2 === 0 ? 1 : 0"
      >
        <div class="d-flex align-center mb-4">
          <v-icon
            size="32"
            :color="idx % 2 === 0 ? 'accent' : 'accent'"
            class="mr-3"
          >{{ cat.icon }}</v-icon>
          <h2
            class="section-title"
            :class="idx % 2 === 0 ? 'text-primary' : 'text-accent'"
          >{{ cat.title }}</h2>
        </div>
        <p
          class="body-text mb-4"
          :class="idx % 2 !== 0 ? 'text-accent' : ''"
          style="opacity: 0.85"
        >{{ cat.description }}</p>

        <!-- Image placeholder grid -->
        <v-row v-if="cat.images && cat.images.length" class="mt-4">
          <v-col
            v-for="(img, imgIdx) in cat.images"
            :key="imgIdx"
            cols="12"
            sm="6"
            md="4"
          >
            <v-card class="card-clean overflow-hidden" elevation="1">
              <v-img
                :src="img.src"
                :alt="img.alt"
                :aspect-ratio="4/3"
                cover
              ></v-img>
              <div class="pa-3" v-if="img.caption">
                <p class="caption-text">{{ img.caption }}</p>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Placeholder for future images -->
        <div v-else class="image-placeholder mt-4">
          <v-icon size="40" :color="idx % 2 === 0 ? 'secondary' : 'accent'" style="opacity: 0.3">mdi-image-multiple</v-icon>
          <p
            class="caption-text mt-2"
            :class="idx % 2 !== 0 ? 'text-accent' : ''"
            style="opacity: 0.3"
          >Bilder folgen</p>
        </div>

        <!-- Subcategories / Tags -->
        <div v-if="cat.tags && cat.tags.length" class="mt-4 d-flex flex-wrap ga-2">
          <v-chip
            v-for="tag in cat.tags"
            :key="tag"
            size="small"
            :variant="idx % 2 === 0 ? 'tonal' : 'outlined'"
            :color="idx % 2 === 0 ? 'secondary' : 'accent'"
          >{{ tag }}</v-chip>
        </div>

        <!-- Future: Configurator slot -->
        <div v-if="cat.configurator" class="mt-6 pa-4 configurator-placeholder card-clean">
          <v-icon class="mr-2" color="accent">mdi-cog</v-icon>
          <span class="body-text">Konfigurator kommt bald</span>
        </div>
      </v-card>
    </section>

    <!-- CTA -->
    <v-card class="bg-primary pa-6 pa-md-8 my-8 card-clean text-center" elevation="0">
      <h2 class="text-accent mb-2">Ihr Projekt – unsere Leidenschaft</h2>
      <p class="body-text text-accent mb-5" style="opacity: 0.85">
        Egal ob Einzelmöbel, kompletter Innenausbau oder Sonderlösung –
        sprechen Sie uns an und wir finden gemeinsam die beste Lösung.
      </p>
      <div class="d-flex flex-column flex-md-row justify-center align-center">
        <v-btn
          size="large"
          class="bg-accent text-primary ma-2"
          href="tel:+4915778204841"
        >
          <v-icon start>mdi-phone</v-icon>
          +49 1577 8204841
        </v-btn>
        <v-btn
          size="large"
          class="bg-accent text-primary ma-2"
          to="/contact"
        >
          <v-icon start>mdi-email</v-icon>
          Jetzt Kontakt aufnehmen
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "Leistungen",
  data() {
    return {
      categories: [
        {
          id: "kuechen",
          title: "Küchen",
          icon: "mdi-stove",
          description:
            "Ihre Küche ist das Herzstück Ihres Zuhauses – wir planen und fertigen sie exakt nach Ihren Vorstellungen. Von der Arbeitsplatte aus Massivholz bis zum Einbauschrank nutzen wir jeden Zentimeter optimal. Unsere Küchen verbinden Funktionalität mit handwerklicher Qualität.",
          tags: ["Massivholz-Arbeitsplatten", "Einbauküchen", "Kücheninseln", "Vorratsschränke"],
          images: [],
          configurator: false,
        },
        {
          id: "wohnzimmer",
          title: "Wohnzimmer",
          icon: "mdi-sofa",
          description:
            "Individuelle Wohnzimmermöbel, die Ihren Raum perfekt ergänzen. Ob Sideboard, Couchtisch, Regal oder komplette Wohnwand – jedes Stück wird passgenau für Ihren Wohnraum gefertigt und verleiht Ihrem Zuhause einen einzigartigen Charakter.",
          tags: ["Sideboards", "Couchtische", "Regale", "Wohnwände", "TV-Möbel"],
          images: [],
          configurator: false,
        },
        {
          id: "esszimmer",
          title: "Esszimmer",
          icon: "mdi-silverware-fork-knife",
          description:
            "Der Esstisch ist der Mittelpunkt des Familienlebens. Wir fertigen Massivholztische in jeder Größe und Form – von der rustikalen Bohlenplatte bis zum filigranen Design. Dazu passende Bänke, Stühle und Anrichten.",
          tags: ["Esstische", "Bänke", "Anrichten", "Vitrinen"],
          images: [],
          configurator: false,
        },
        {
          id: "schlafzimmer",
          title: "Schlafzimmer",
          icon: "mdi-bed",
          description:
            "Ihr Schlafzimmer soll ein Ort der Ruhe sein. Wir bauen Betten, Nachttische und begehbare Kleiderschränke, die sich nahtlos in Ihren Raum einfügen. Natürliche Materialien und sorgfältige Verarbeitung sorgen für ein gesundes Raumklima.",
          tags: ["Massivholzbetten", "Nachttische", "Kleiderschränke", "Kommoden"],
          images: [],
          configurator: false,
        },
        {
          id: "bad",
          title: "Badezimmer",
          icon: "mdi-shower",
          description:
            "Badmöbel aus Holz bringen Wärme in jeden Nassraum. Waschtischunterschränke, Spiegelschränke und Regale fertigen wir passgenau und mit wasserbeständigen Oberflächen. Auch besondere Grundrisse und Dachschrägen sind für uns kein Problem.",
          tags: ["Waschtische", "Spiegelschränke", "Badregale", "Dachschrägenlösungen"],
          images: [],
          configurator: false,
        },
        {
          id: "massivholzmoebel",
          title: "Massivholzmöbel",
          icon: "mdi-tree",
          description:
            "Massivholz ist unser Werkstoff der Leidenschaft. Jedes Stück Holz ist einzigartig – und genau das macht unsere Möbel besonders. Wir verarbeiten heimische Hölzer wie Eiche, Nussbaum, Esche und Kirschbaum zu langlebigen Einzelstücken mit Charakter.",
          tags: ["Eiche", "Nussbaum", "Esche", "Kirschbaum", "Lärche", "Ahorn"],
          images: [],
          configurator: false,
        },
        {
          id: "einbauschraenke",
          title: "Einbauschränke & Garderoben",
          icon: "mdi-wardrobe",
          description:
            "Maßgefertigte Einbauschränke nutzen jeden Winkel Ihres Zuhauses – ob unter der Treppe, in der Dachschräge oder als raumhohe Garderobe. Wir planen die Innenaufteilung gemeinsam mit Ihnen, damit alles seinen Platz findet.",
          tags: ["Einbauschränke", "Garderoben", "Dachschrägen", "Nischenschränke", "Windfänge"],
          images: [],
          configurator: false,
        },
        {
          id: "innenausbau",
          title: "Kompletter Innenausbau",
          icon: "mdi-home-city",
          description:
            "Vom Bodenbelag über Wandverkleidungen bis zur Deckengestaltung – wir übernehmen den kompletten Innenausbau aus einer Hand. Das spart Ihnen Koordinationsaufwand und garantiert ein stimmiges Gesamtbild.",
          tags: ["Wandverkleidungen", "Deckenverkleidungen", "Türen", "Treppen"],
          images: [],
          configurator: false,
        },
        {
          id: "hotellerie",
          title: "Hotellerie & Gastronomie",
          icon: "mdi-silverware",
          description:
            "Für Hotels und Restaurants fertigen wir vom Empfangstresen über Zimmerausstattungen bis zur Bareinrichtung. Wir kennen die Anforderungen an Langlebigkeit, Hygiene und Design im gewerblichen Einsatz und setzen Ihre Projekte termingerecht um.",
          tags: ["Hotelzimmer", "Restaurants", "Empfangsbereiche", "Bars"],
          images: [],
          configurator: false,
        },
        {
          id: "ladenbau",
          title: "Ladenbau & Objektplanung",
          icon: "mdi-store",
          description:
            "Ihr Geschäft ist Ihre Visitenkarte. Wir gestalten Verkaufsräume, Theken, Regalsysteme und Präsentationsmöbel, die Ihre Produkte optimal in Szene setzen. Von der Planung bis zur schlüsselfertigen Übergabe.",
          tags: ["Ladeneinrichtungen", "Theken", "Regalsysteme", "Schaufenster"],
          images: [],
          configurator: false,
        },
        {
          id: "industrieklettern",
          title: "Industrieklettern & Seilzugangstechnik",
          icon: "mdi-carabiner",
          description:
            "Dank ausgebildeter Industriekletterer erreichen wir auch schwer zugängliche Stellen. Ob Fassadenarbeiten, Montagen in großer Höhe oder Holzverkleidungen an Gebäuden – dort wo Gerüste an ihre Grenzen stoßen, kommen wir zum Einsatz.",
          tags: ["Fassadenarbeiten", "Höhenmontage", "Holzverkleidungen"],
          images: [],
          configurator: false,
        },
      ],
    };
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
};
</script>

<style scoped>
.page-title {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: clamp(1.5em, 5vw, 2.5em);
  font-weight: 400;
}

.page-subtitle {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 1em;
  opacity: 0.6;
  margin-top: 0.5rem;
}

.card-clean {
  border-radius: 12px;
}

.section-title {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-weight: 400;
  font-size: 1.4em;
}

.body-text {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 1em;
  line-height: 1.7;
}

.caption-text {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
  font-size: 0.85em;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.configurator-placeholder {
  display: inline-flex;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
}

.service-section {
  scroll-margin-top: 80px;
}
</style>
